import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { WorkspaceType } from '@/api/onboarding/onboardingApi.types';
import { BrandLogo } from '@/components/common/BrandLogo';
import { Icon } from '@/components/common/Icon';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/passwordInput';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { LegalConsentCheckbox } from '@/components/legal/LegalConsentCheckbox';
import { LegalLinks } from '@/components/legal/LegalLinks';
import { useRegisterWorkspaceMutation } from '@/hooks/useOnboardingMutations';
import { useAuth } from '@/hooks/useAuth';
import { isValidEmail } from '@/utils/formValidation';

const MINIMUM_PASSWORD_LENGTH = 10;

export function SignUpScreen() {
  const { t } = useTranslation(['onboarding', 'auth', 'admin', 'legal']);
  const navigate = useNavigate();
  const { status, completeSessionWithTokens } = useAuth();
  const registerMutation = useRegisterWorkspaceMutation();

  const [step, setStep] = useState(1);
  const [tenantType, setTenantType] = useState<WorkspaceType>('solo');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  if (status === 'authenticated') {
    return <Navigate to={APPLICATION_ROUTES.workspaceDashboard} replace />;
  }

  function goToAccountStep() {
    setErrorMessage('');
    setStep(2);
  }

  function goToWorkspaceStep() {
    setErrorMessage('');
    if (fullName.trim().length === 0 || password.length < MINIMUM_PASSWORD_LENGTH) {
      setErrorMessage(t('onboarding:signUp.accountValidation', { count: MINIMUM_PASSWORD_LENGTH }));
      return;
    }
    if (isValidEmail(email) === false) {
      setErrorMessage(t('validation:email'));
      return;
    }
    setStep(3);
  }

  async function submitRegistration() {
    setErrorMessage('');
    if (workspaceName.trim().length === 0) {
      setErrorMessage(t('onboarding:signUp.workspaceValidation'));
      return;
    }
    if (tenantType === 'organization' && organizationName.trim().length === 0) {
      setErrorMessage(t('onboarding:signUp.organizationValidation'));
      return;
    }
    if (hasAcceptedTerms === false) {
      setErrorMessage(t('legal:consentRequired.owner'));
      return;
    }
    try {
      const registration = await registerMutation.mutateAsync({
        tenantType,
        workspaceName: workspaceName.trim(),
        organizationName: tenantType === 'organization' ? organizationName.trim() : null,
        fullName: fullName.trim(),
        email: email.trim(),
        password,
        phone: null,
        preferredLanguage: i18next.language,
        acceptTerms: hasAcceptedTerms,
      });
      await completeSessionWithTokens(registration.tokens);
      navigate(APPLICATION_ROUTES.workspaceDashboard, { replace: true });
    } catch (registrationError) {
      const backendMessage = resolveApiErrorMessage(registrationError);
      setErrorMessage(backendMessage.length > 0 ? backendMessage : t('admin:errors.generic'));
    }
  }

  return (
    <div className="min-h-screen bg-zoooffwhite flex items-center justify-center p-4">
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md bg-zoowhite border border-zooborder rounded-[2.5rem] shadow-xl overflow-hidden">
        <div className="p-8 md:p-10">
          <div className="flex justify-center mb-6">
            <BrandLogo className="h-10 w-auto object-contain" />
          </div>
          <StepProgress step={step} />

          {step === 1 ? (
            <ChooseTypeStep
              tenantType={tenantType}
              onSelect={setTenantType}
              onContinue={goToAccountStep}
            />
          ) : null}

          {step === 2 ? (
            <AccountStep
              fullName={fullName}
              email={email}
              password={password}
              onFullName={setFullName}
              onEmail={setEmail}
              onPassword={setPassword}
              onBack={function backToType() {
                setErrorMessage('');
                setStep(1);
              }}
              onContinue={goToWorkspaceStep}
              errorMessage={errorMessage}
            />
          ) : null}

          {step === 3 ? (
            <WorkspaceStep
              tenantType={tenantType}
              workspaceName={workspaceName}
              organizationName={organizationName}
              onWorkspaceName={setWorkspaceName}
              onOrganizationName={setOrganizationName}
              hasAcceptedTerms={hasAcceptedTerms}
              onAcceptTerms={setHasAcceptedTerms}
              onBack={function backToAccount() {
                setErrorMessage('');
                setStep(2);
              }}
              onSubmit={submitRegistration}
              isSubmitting={registerMutation.isPending}
              errorMessage={errorMessage}
            />
          ) : null}

          <div className="mt-6 text-center">
            <span className="text-sm text-zoomuted">{t('onboarding:signUp.haveAccount')} </span>
            <Button asChild variant="link" className="text-sm">
              <Link to={APPLICATION_ROUTES.login}>{t('onboarding:signUp.signIn')}</Link>
            </Button>
          </div>
          <LegalLinks className="mt-4" />
        </div>
      </div>
    </div>
  );
}

function StepProgress({ step }: { step: number }) {
  return (
    <div className="flex items-center gap-1.5 mb-6">
      {[1, 2, 3].map(function renderSegment(segment) {
        return (
          <span
            key={segment}
            className={
              'flex-1 h-1.5 rounded-full ' + (segment <= step ? 'bg-zooprimary' : 'bg-zooborder')
            }
          />
        );
      })}
    </div>
  );
}

interface ChooseTypeStepProps {
  tenantType: WorkspaceType;
  onSelect: (nextType: WorkspaceType) => void;
  onContinue: () => void;
}

function ChooseTypeStep({ tenantType, onSelect, onContinue }: ChooseTypeStepProps) {
  const { t } = useTranslation('onboarding');
  return (
    <>
      <h1 className="text-xl font-bold text-zooindigo mb-1">{t('signUp.step1Title')}</h1>
      <p className="text-sm text-zoomuted mb-5">{t('signUp.step1Hint')}</p>
      <div className="space-y-3" role="radiogroup" aria-label={t('signUp.accountTypeGroupLabel')}>
        <TypeOption
          isSelected={tenantType === 'solo'}
          iconName="UserRound"
          title={t('signUp.typeSoloTitle')}
          description={t('signUp.typeSoloDescription')}
          onSelect={function selectSolo() {
            onSelect('solo');
          }}
        />
        <TypeOption
          isSelected={tenantType === 'organization'}
          iconName="Building"
          title={t('signUp.typeOrgTitle')}
          description={t('signUp.typeOrgDescription')}
          onSelect={function selectOrganization() {
            onSelect('organization');
          }}
        />
      </div>
      <Button
        size="large"
        onClick={onContinue}
        className="w-full mt-6 flex items-center justify-center gap-2"
      >
        {t('signUp.continue')} <Icon name="ArrowRight" />
      </Button>
    </>
  );
}

interface TypeOptionProps {
  isSelected: boolean;
  iconName: 'UserRound' | 'Building';
  title: string;
  description: string;
  onSelect: () => void;
}

function TypeOption({ isSelected, iconName, title, description, onSelect }: TypeOptionProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      onClick={onSelect}
      className={
        'w-full text-left p-4 rounded-2xl border flex items-start gap-3 transition-colors ' +
        (isSelected === true
          ? 'border-2 border-zooprimary bg-zoovpalelavender'
          : 'border border-zooborder hover:bg-zoooffwhite')
      }
    >
      <span className="w-9 h-9 rounded-xl bg-zoowhite flex items-center justify-center shrink-0">
        <Icon name={iconName} className="text-zooprimary" />
      </span>
      <span className="flex-1">
        <span className="block font-bold text-zooindigo text-sm">{title}</span>
        <span className="block text-xs text-zoomuted mt-0.5">{description}</span>
      </span>
      <span
        className={
          'w-5 h-5 rounded-full shrink-0 mt-0.5 flex items-center justify-center ' +
          (isSelected === true ? 'bg-zooprimary' : 'border-2 border-zooborder')
        }
      >
        {isSelected === true ? <Icon name="Check" className="text-zoowhite text-xs" /> : null}
      </span>
    </button>
  );
}

interface AccountStepProps {
  fullName: string;
  email: string;
  password: string;
  onFullName: (value: string) => void;
  onEmail: (value: string) => void;
  onPassword: (value: string) => void;
  onBack: () => void;
  onContinue: () => void;
  errorMessage: string;
}

function AccountStep(props: AccountStepProps) {
  const { t } = useTranslation(['onboarding', 'auth']);
  return (
    <>
      <h1 className="text-xl font-bold text-zooindigo mb-5">{t('onboarding:signUp.accountTitle')}</h1>
      <div className="space-y-3.5">
        <div className="space-y-1.5">
          <Label htmlFor="signup-name">{t('onboarding:signUp.fullNameLabel')}</Label>
          <Input
            id="signup-name"
            value={props.fullName}
            onChange={function updateName(changeEvent) {
              props.onFullName(changeEvent.target.value);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="signup-email">{t('onboarding:signUp.emailLabel')}</Label>
          <Input
            id="signup-email"
            type="email"
            value={props.email}
            placeholder={t('auth:emailPlaceholder')}
            onChange={function updateEmail(changeEvent) {
              props.onEmail(changeEvent.target.value);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="signup-password">{t('onboarding:signUp.passwordLabel')}</Label>
          <PasswordInput
            id="signup-password"
            value={props.password}
            placeholder={t('auth:passwordPlaceholder')}
            onChange={function updatePassword(changeEvent) {
              props.onPassword(changeEvent.target.value);
            }}
          />
        </div>
      </div>
      <StepError message={props.errorMessage} />
      <StepButtons onBack={props.onBack} onNext={props.onContinue} nextLabel={t('onboarding:signUp.continue')} />
    </>
  );
}

interface WorkspaceStepProps {
  tenantType: WorkspaceType;
  workspaceName: string;
  organizationName: string;
  onWorkspaceName: (value: string) => void;
  onOrganizationName: (value: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  errorMessage: string;
  hasAcceptedTerms: boolean;
  onAcceptTerms: (hasAccepted: boolean) => void;
}

function WorkspaceStep(props: WorkspaceStepProps) {
  const { t } = useTranslation('onboarding');
  const isOrganization = props.tenantType === 'organization';
  return (
    <>
      <h1 className="text-xl font-bold text-zooindigo mb-5">
        {isOrganization === true ? t('signUp.workspaceTitleOrg') : t('signUp.workspaceTitleSolo')}
      </h1>
      <div className="space-y-3.5">
        {isOrganization === true ? (
          <div className="space-y-1.5">
            <Label htmlFor="signup-org">{t('signUp.organizationNameLabel')}</Label>
            <Input
              id="signup-org"
              value={props.organizationName}
              onChange={function updateOrg(changeEvent) {
                props.onOrganizationName(changeEvent.target.value);
              }}
            />
          </div>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor="signup-workspace">{t('signUp.workspaceNameLabel')}</Label>
          <Input
            id="signup-workspace"
            value={props.workspaceName}
            onChange={function updateWorkspace(changeEvent) {
              props.onWorkspaceName(changeEvent.target.value);
            }}
          />
        </div>
        <LegalConsentCheckbox
          audience="owner"
          isChecked={props.hasAcceptedTerms}
          onChange={props.onAcceptTerms}
        />
      </div>
      <StepError message={props.errorMessage} />
      <div className="flex gap-3 mt-6">
        <Button variant="outline" onClick={props.onBack}>
          {t('signUp.back')}
        </Button>
        <Button isLoading={props.isSubmitting} onClick={props.onSubmit} className="flex-1">
          {t('signUp.create')}
        </Button>
      </div>
    </>
  );
}

function StepError({ message }: { message: string }) {
  if (message.length === 0) {
    return null;
  }
  return (
    <div
      role="alert"
      className="mt-4 flex items-center gap-2 px-3.5 py-2.5 bg-zooerrorbg border border-zooerrorborder rounded-xl text-xs font-medium text-zooerrortext"
    >
      <Icon name="AlertCircle" />
      {message}
    </div>
  );
}

interface StepButtonsProps {
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
}

function StepButtons({ onBack, onNext, nextLabel }: StepButtonsProps) {
  const { t } = useTranslation('onboarding');
  return (
    <div className="flex gap-3 mt-6">
      <Button variant="outline" onClick={onBack}>
        {t('signUp.back')}
      </Button>
      <Button onClick={onNext} className="flex-1 flex items-center justify-center gap-2">
        {nextLabel} <Icon name="ArrowRight" />
      </Button>
    </div>
  );
}
